import React, { useEffect, useState } from "react";
import { FiDownload, FiTrash2, FiLoader, FiMic, FiInfo, FiWind, FiStar } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { UploadCard, useJobHistory, formatSeconds } from "./audioShared";

const MAX_AUDIO_BYTES = 80 * 1024 * 1024;

const MODES = [
  {
    key: "standard",
    title: "Remove noise",
    icon: FiWind,
    text: "Takes out background noise, hum, hiss, wind, room echo and clicks while keeping the voice natural.",
    meta: "Fast - about as long as the recording. Up to 15 minutes.",
  },
  {
    key: "studio",
    title: "Studio clarity",
    icon: FiStar,
    text: "Removes noise and also rebuilds a clearer, fuller voice. Best for a weak or muffled recording.",
    meta: "Uses a shared GPU, so it can be unavailable at busy times. Clips up to 45 seconds.",
  },
];

// AI Noise Removal and Audio Enhancement: a noisy recording in, a clean and clearer one out. The work runs on
// the server in the background (long recordings are cleaned in pieces) and this page polls for the result.
export default function NoiseRemovalTool() {
  const { clientId, history, loading, running, latest, elapsed, add, remove } = useJobHistory("enhancement");
  const [audio, setAudio] = useState(null);
  const [mode, setMode] = useState("standard");
  const [normalize, setNormalize] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fileError, setFileError] = useState("");
  const [originalUrl, setOriginalUrl] = useState("");

  // Keeps the picked file playable so the result can be compared with the original.
  useEffect(() => {
    if (!audio) { setOriginalUrl(""); return undefined; }
    const url = URL.createObjectURL(audio);
    setOriginalUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [audio]);

  const canSubmit = audio && !submitting && !running;

  const submit = async () => {
    setError("");
    setFileError("");
    setSubmitting(true);
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "enhancement");
    form.append("original", audio);
    form.append("mode", mode);
    form.append("normalize", String(normalize));
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) {
      if (data?.original) return setFileError(data.original);
      return setError(errorMessage(data));
    }
    add(data);
  };

  const showResult = latest && latest.status === "completed" && latest.result_audio && !running;
  const modeLabel = (job) => (job.quality === "studio" ? "Studio clarity" : "Remove noise");

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Noise Removal and Audio Enhancement</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
        Upload a noisy recording - a call, interview, lecture, video voice-over or voice note - and get it back clean, with
        the background noise and distortion removed and the voice clearer.
      </p>

      <UploadCard
        step={1} kind={mode === "studio" ? "studio" : "noisy"} icon={FiMic} title="Your recording"
        help="Voice recordings work best. WAV, MP3, FLAC, OGG or M4A, up to 80 MB."
        picked={audio} onPick={setAudio} onClear={() => setAudio(null)} problem={fileError}
        maxBytes={MAX_AUDIO_BYTES}
      />

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              className={`text-left rounded-2xl border p-5 transition-colors ${active ? "border-accent-light bg-accent/10" : "border-navy-border bg-white/5 hover:border-accent-light/60"}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className={`w-9 h-9 rounded-full flex items-center justify-center ${active ? "bg-brand-gradient" : "bg-white/10"}`}><Icon size={17} /></span>
                <span className="font-semibold">{m.title}</span>
              </div>
              <p className="text-sm text-gray-300">{m.text}</p>
              <p className="text-xs text-gray-500 mt-2">{m.meta}</p>
            </button>
          );
        })}
      </div>

      <label className="flex items-start gap-2.5 text-sm text-gray-300 cursor-pointer select-none mt-6">
        <input type="checkbox" checked={normalize} onChange={(e) => setNormalize(e.target.checked)} className="mt-0.5 accent-accent-light" />
        <span>
          Even out the volume
          <span className="block text-xs text-gray-500">Brings a quiet voice up to a comfortable level, without distortion.</span>
        </span>
      </label>

      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6">{error}</p>}

      <button
        onClick={submit}
        disabled={!canSubmit}
        className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? <><FiLoader className="animate-spin" /> Uploading...</> : running ? <><FiLoader className="animate-spin" /> Cleaning...</> : <><FiWind /> Clean my audio</>}
      </button>
      {!canSubmit && !running && !submitting && <p className="text-xs text-gray-500 text-center mt-2">Add your recording first</p>}

      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <div className="flex items-center gap-3">
            <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
            <div>
              <p className="font-semibold">
                Cleaning your audio... {formatSeconds(elapsed)}
                {running.progress && <span className="text-gray-400 font-normal"> · part {running.progress.replace("/", " of ")}</span>}
              </p>
              <p className="text-sm text-gray-400">
                {running.quality === "studio" ? "Studio clarity can take a few minutes." : "This takes about as long as the recording."} You can stay on this page.
              </p>
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 mt-4 overflow-hidden">
            {running.progress ? (
              <div className="h-full rounded-full bg-brand-gradient transition-all" style={{ width: `${(Number(running.progress.split("/")[0]) / Number(running.progress.split("/")[1])) * 100}%` }} />
            ) : (
              <div className="h-full w-1/3 rounded-full bg-brand-gradient animate-pulse" />
            )}
          </div>
        </div>
      )}

      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3">
          <FiInfo className="mt-0.5 shrink-0" />
          <span>{latest.error_message || "The recording could not be cleaned. Please try again."}</span>
        </div>
      )}

      {showResult && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your clean audio</p>
          <div className="grid md:grid-cols-2 gap-5">
            {originalUrl && (
              <div>
                <p className="text-sm text-gray-400 mb-2">Before (original)</p>
                <audio controls src={originalUrl} className="w-full" />
              </div>
            )}
            <div className={originalUrl ? "" : "md:col-span-2"}>
              <p className="text-sm text-emerald-300 mb-2">After ({modeLabel(latest)})</p>
              <audio controls src={latest.result_audio} className="w-full" />
            </div>
          </div>
          <a href={latest.result_audio} download className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full border border-white/20 hover:border-accent-light text-white text-sm font-medium transition-colors">
            <FiDownload /> Download clean audio
          </a>
          <p className="text-xs text-gray-500 mt-3">
            Heavy noise on top of a very quiet voice can leave a little behind. If the result sounds thin, try the other mode.
          </p>
        </div>
      )}

      <div className="mt-10">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your clean recordings</p>
        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing here yet. Your results will appear here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {history.map((job) => (
              <div key={job.id} className="rounded-xl bg-white/5 border border-navy-border p-4">
                <p className="text-sm text-gray-200 truncate">{job.original_name || "Recording"}</p>
                <p className="text-xs text-gray-500 mb-2">
                  {modeLabel(job)} · {new Date(job.created_at).toLocaleString()} · {job.status === "completed" ? "Ready" : job.status === "failed" ? "Failed" : "Working..."}
                </p>
                {job.status === "completed" && job.result_audio && (
                  <div className="flex items-center gap-3">
                    <audio controls src={job.result_audio} className="w-full h-8" />
                    <a href={job.result_audio} download title="Download" className="text-gray-400 hover:text-accent-light shrink-0"><FiDownload size={16} /></a>
                    <button onClick={() => remove(job)} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={16} /></button>
                  </div>
                )}
                {job.status === "failed" && (
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-xs text-red-300">{job.error_message}</p>
                    <button onClick={() => remove(job)} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={16} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
